import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import type {
  Modality,
  ModalityGroup,
  ModalityGroupInput,
  ModalityInput,
  Product,
  ProductInput,
} from '@/shared/api/resources';
import {
  moneyInputError,
  parseMoney,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';

import { fieldErrorOf } from '../shared/fieldError';
import { Caixa } from '../users/Caixa';
import { dinheiroParaCampo } from './campos';
import {
  gruposDeCreditoDaModalidade,
  gruposDeCreditoDoGrupo,
  referenciasDeCredito,
  situacoesPara,
  tiposDeRamo,
} from './rotulos';

function Rodape({
  submitting,
  onCancel,
  confirmar,
  bloqueado = false,
}: {
  submitting: boolean;
  onCancel: () => void;
  confirmar: string;
  bloqueado?: boolean;
}) {
  return (
    <div className="flex justify-end gap-2 border-t border-line pt-4">
      <Button type="button" variant="secondary" onClick={onCancel}>
        Fechar
      </Button>
      <Button type="submit" loading={submitting} disabled={bloqueado}>
        {confirmar}
      </Button>
    </div>
  );
}

function ErroDoFormulario({ error }: { error: Error | null }) {
  return error ? (
    <Alert tone="danger" title="Nao foi possivel salvar">
      {error.message}
    </Alert>
  ) : null;
}

/**
 * O ramo do OnPoint e o produto do SeguraCore (decisao 1 da secao 11.6 do plano
 * 49). O formulario mantem o que o cadastro de produto tinha (nome, codigo,
 * situacao, descricao) e acrescenta o ramo SUSEP e o processo.
 */
export function ProductForm({
  product,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  product?: Product | undefined;
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: ProductInput) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(product?.code ?? '');
  const [name, setName] = useState(product?.name ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [status, setStatus] = useState(product?.status ?? 'ativo');
  const [ramo, setRamo] = useState(product?.susep_branch_code ?? '');
  const [grupoDeRamos, setGrupoDeRamos] = useState(product?.susep_group_code ?? '');
  const [processo, setProcesso] = useState(product?.susep_process_number ?? '');
  const [tipo, setTipo] = useState<string>(product?.branch_kind ?? '');

  const apiError = error instanceof ApiError ? error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    onSubmit({
      code,
      name,
      description,
      status,
      susep_branch_code: ramo,
      susep_group_code: grupoDeRamos,
      susep_process_number: processo,
      branch_kind: tipo,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <ErroDoFormulario error={error} />
      <fieldset disabled={submitting} className="flex flex-col gap-4">
        <Field
          label="Nome"
          required
          value={name}
          error={fieldErrorOf(apiError, 'name')}
          hint="Como o ramo aparece na lista, por exemplo Garantia Segurado - Setor Publico."
          onChange={(event) => setName(event.target.value)}
        />
        <Field
          label="Codigo"
          required
          value={code}
          error={fieldErrorOf(apiError, 'code')}
          hint="Minusculas, numeros e hifen. Usado em integracoes e relatorios."
          onChange={(event) => setCode(event.target.value)}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Codigo do ramo SUSEP"
            inputMode="numeric"
            maxLength={4}
            value={ramo}
            error={fieldErrorOf(apiError, 'susep_branch_code')}
            onChange={(event) => setRamo(event.target.value)}
          />
          <Field
            label="Grupo de ramos SUSEP"
            inputMode="numeric"
            maxLength={2}
            value={grupoDeRamos}
            error={fieldErrorOf(apiError, 'susep_group_code')}
            onChange={(event) => setGrupoDeRamos(event.target.value)}
          />
        </div>
        <Field
          label="Numero do processo SUSEP"
          maxLength={60}
          value={processo}
          error={fieldErrorOf(apiError, 'susep_process_number')}
          onChange={(event) => setProcesso(event.target.value)}
        />
        <Select
          label="Tipo do ramo"
          options={tiposDeRamo}
          value={tipo}
          onChange={(event) => setTipo(event.target.value)}
        />
        <Select
          label="Situacao"
          options={situacoesPara(product?.status)}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        />
        <Textarea
          label="Descricao"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </fieldset>
      <Rodape
        submitting={submitting}
        onCancel={onCancel}
        confirmar={product ? 'Salvar' : 'Cadastrar'}
      />
    </form>
  );
}

/**
 * "Cadastrar grupo de modalidades" e "Editar grupo". O nome e o campo do dialogo do
 * OnPoint; os demais vem do ModalityGroup/GetAll e ficam guardados, sem efeito ate a
 * homologacao das regras que os usam.
 */
export function GroupForm({
  group,
  ramoEmRascunho = false,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  group?: ModalityGroup | undefined;
  /** O ramo do grupo novo esta em rascunho: o grupo nasce em rascunho (R5-B32). */
  ramoEmRascunho?: boolean;
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: ModalityGroupInput) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(group?.name ?? '');
  const [creditGroup, setCreditGroup] = useState(group?.credit_group ?? '');
  // Debaixo de ramo em rascunho, o grupo ativo e recusado: a tela ja sugere rascunho.
  const [status, setStatus] = useState(
    group?.status ?? (ramoEmRascunho ? 'rascunho' : 'ativo'),
  );
  const [limite, setLimite] = useState(
    dinheiroParaCampo(group?.default_economic_group_limit ?? null),
  );
  const [codigoExterno, setCodigoExterno] = useState(
    group?.external_product_code ?? '',
  );
  const [ignorarPF, setIgnorarPF] = useState(
    group?.ignore_branch_for_individual_insured ?? false,
  );
  const [ignorarPrivado, setIgnorarPrivado] = useState(
    group?.ignore_branch_for_private_insured ?? false,
  );
  const [descricao, setDescricao] = useState(group?.description ?? '');

  const apiError = error instanceof ApiError ? error : null;
  const erroDoLimite = moneyInputError(limite);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (erroDoLimite) return;
    onSubmit({
      name,
      credit_group: creditGroup,
      status,
      default_economic_group_limit: limite.trim() === '' ? null : parseMoney(limite),
      external_product_code: codigoExterno,
      ignore_branch_for_individual_insured: ignorarPF,
      ignore_branch_for_private_insured: ignorarPrivado,
      description: descricao,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <ErroDoFormulario error={error} />
      <fieldset disabled={submitting} className="flex flex-col gap-4">
        {group ? (
          <Field
            label="Nome do grupo"
            required
            value={name}
            error={fieldErrorOf(apiError, 'name')}
            onChange={(event) => setName(event.target.value)}
          />
        ) : (
          <Field
            label="Nome do novo grupo"
            required
            value={name}
            error={fieldErrorOf(apiError, 'name')}
            onChange={(event) => setName(event.target.value)}
          />
        )}
        {/* ModalityGroupDescription do OnPoint (TOM-645): o texto do icone de
            informacao do grupo, ao lado dos limites do tomador. */}
        <Textarea
          label="Descricao do grupo"
          maxLength={2000}
          value={descricao}
          error={fieldErrorOf(apiError, 'description')}
          onChange={(event) => setDescricao(event.target.value)}
        />
        <Select
          label="Grupo de credito"
          options={gruposDeCreditoDoGrupo}
          value={creditGroup}
          error={fieldErrorOf(apiError, 'credit_group')}
          onChange={(event) => setCreditGroup(event.target.value)}
        />
        <Select
          label="Situacao do grupo"
          options={situacoesPara(group?.status)}
          value={status}
          hint={
            !group && ramoEmRascunho && status !== 'rascunho'
              ? 'O ramo esta em rascunho: o grupo ativo debaixo dele e recusado.'
              : undefined
          }
          onChange={(event) => setStatus(event.target.value)}
        />
        <MoneyField
          label="Limite padrao de grupo economico"
          value={limite}
          error={erroDoLimite ?? fieldErrorOf(apiError, 'default_economic_group_limit')}
          onValueChange={setLimite}
        />
        <Field
          label="Codigo do produto na seguradora"
          maxLength={40}
          value={codigoExterno}
          error={fieldErrorOf(apiError, 'external_product_code')}
          onChange={(event) => setCodigoExterno(event.target.value)}
        />
        <Caixa
          label="Ignorar ramo quando o segurado e pessoa fisica"
          checked={ignorarPF}
          onChange={setIgnorarPF}
        />
        <Caixa
          label="Ignorar ramo quando o segurado e privado"
          checked={ignorarPrivado}
          onChange={setIgnorarPrivado}
        />
      </fieldset>
      <Rodape
        submitting={submitting}
        onCancel={onCancel}
        confirmar="Salvar"
        bloqueado={erroDoLimite !== undefined}
      />
    </form>
  );
}

/**
 * "Adicionar modalidade" e a edicao rapida do cadastro. Mantem os campos que o
 * cadastro de modalidade tinha em /produtos e acrescenta o grupo, o codigo de
 * referencia da seguradora e a classificacao de credito, que o tipo da tela nao
 * tinha (defeito 7 do anexo C do 47).
 */
export function ModalityForm({
  modality,
  groups,
  defaultGroupId,
  ramoEmRascunho = false,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  modality?: Modality | undefined;
  groups: ModalityGroup[];
  /** O ramo da modalidade nova esta em rascunho (R5-B32). */
  ramoEmRascunho?: boolean;
  defaultGroupId?: string | undefined;
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: ModalityInput) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(modality?.code ?? '');
  const [name, setName] = useState(modality?.name ?? '');
  const [description, setDescription] = useState(modality?.description ?? '');
  const [grupo, setGrupo] = useState(
    modality ? (modality.modality_group_id ?? '') : (defaultGroupId ?? ''),
  );
  const grupoEmRascunho = (id: string) =>
    groups.some((g) => g.id === id && g.status === 'rascunho');
  // Debaixo de ramo ou grupo em rascunho, a modalidade ativa e recusada: a tela ja
  // sugere rascunho (R5-B32).
  const [status, setStatus] = useState(
    modality?.status ??
      (ramoEmRascunho || grupoEmRascunho(grupo) ? 'rascunho' : 'ativo'),
  );
  const paiEmRascunho = ramoEmRascunho || grupoEmRascunho(grupo);
  const [rate, setRate] = useState(modality ? rateToInput(modality.base_rate_bps) : '');
  const [creditGroup, setCreditGroup] = useState<string>(modality?.credit_group ?? '');
  const [creditReference, setCreditReference] = useState(
    modality?.credit_reference ?? '',
  );
  const [referenciaDaSeguradora, setReferenciaDaSeguradora] = useState(
    modality?.insurer_reference_code ?? '',
  );

  const apiError = error instanceof ApiError ? error : null;
  const erroTaxa = rateInputError(rate);

  function escolherReferencia(valor: string) {
    setCreditReference(valor);
    // A referencia pertence a um grupo de credito na fonte; o servidor recusa outra
    // combinacao, e a tela ja escolhe o grupo dela.
    const grupoDaReferencia = referenciasDeCredito.find(
      (r) => r.value === valor,
    )?.grupo;
    if (grupoDaReferencia) setCreditGroup(grupoDaReferencia);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (erroTaxa) return;
    onSubmit({
      code,
      name,
      description,
      status,
      base_rate_bps: rateToBasisPoints(rate || '0'),
      modality_group_id: grupo,
      credit_group: creditGroup,
      credit_reference: creditReference,
      insurer_reference_code: referenciaDaSeguradora,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <ErroDoFormulario error={error} />
      <fieldset disabled={submitting} className="flex flex-col gap-4">
        <Field
          label="Nome"
          required
          value={name}
          error={fieldErrorOf(apiError, 'name')}
          onChange={(event) => setName(event.target.value)}
        />
        <Field
          label="Codigo"
          required
          value={code}
          error={fieldErrorOf(apiError, 'code')}
          onChange={(event) => setCode(event.target.value)}
        />
        <Select
          label="Grupo de modalidades"
          options={[
            { value: '', label: 'Sem grupo' },
            ...groups.map((g) => ({ value: g.id, label: g.name })),
          ]}
          value={grupo}
          error={fieldErrorOf(apiError, 'modality_group_id')}
          onChange={(event) => setGrupo(event.target.value)}
        />
        <Field
          label="Taxa base (%)"
          inputMode="decimal"
          value={rate}
          error={erroTaxa ?? fieldErrorOf(apiError, 'base_rate_bps')}
          hint="Ponto de partida da precificacao. Agravos e descontos ajustam o valor final."
          onChange={(event) => setRate(event.target.value)}
        />
        <Field
          label="Codigo de referencia da seguradora"
          maxLength={40}
          value={referenciaDaSeguradora}
          error={fieldErrorOf(apiError, 'insurer_reference_code')}
          onChange={(event) => setReferenciaDaSeguradora(event.target.value)}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Grupo de credito"
            options={gruposDeCreditoDaModalidade}
            value={creditGroup}
            error={fieldErrorOf(apiError, 'credit_group')}
            onChange={(event) => setCreditGroup(event.target.value)}
          />
          <Select
            label="Referencia de credito"
            options={referenciasDeCredito}
            value={creditReference}
            error={fieldErrorOf(apiError, 'credit_reference')}
            onChange={(event) => escolherReferencia(event.target.value)}
          />
        </div>
        <Select
          label="Situacao"
          options={situacoesPara(modality?.status)}
          value={status}
          hint={
            !modality && paiEmRascunho && status !== 'rascunho'
              ? 'O ramo ou o grupo esta em rascunho: a modalidade ativa debaixo dele e recusada.'
              : undefined
          }
          onChange={(event) => setStatus(event.target.value)}
        />
        <Textarea
          label="Descricao"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </fieldset>
      <Rodape
        submitting={submitting}
        onCancel={onCancel}
        confirmar={modality ? 'Salvar' : 'Cadastrar'}
        bloqueado={erroTaxa !== undefined}
      />
    </form>
  );
}
